
import Vue from "vue";
import JSZip from 'jszip';
import parser from 'fast-xml-parser';
import { OTA_TERMINAL_STATUSES } from './bed-manager-constants';

const BM_VERSION = process.env.VUE_APP_BM_VERSION || '0.0.0-local';

// Numeric, segment-wise version compare (left-to-right, missing segments treated as 0).
// Strips a leading 'v'/'V' since gatewaySWVersionNumber values may be prefixed (e.g. "v03.05.000.01")
// while config.phase2GWVersion is not.
const compareGatewayVersions = (a, b) => {
  const strip = (v) => String(v).replace(/^v/i, '');
  const partsA = strip(a).split('.').map((seg) => parseInt(seg, 10) || 0);
  const partsB = strip(b).split('.').map((seg) => parseInt(seg, 10) || 0);
  const len = Math.max(partsA.length, partsB.length);
  for (let i = 0; i < len; i++) {
    const diff = (partsA[i] || 0) - (partsB[i] || 0);
    if (diff !== 0) return diff < 0 ? -1 : 1;
  }
  return 0;
};

const BedManagerService = class {
  _DEBUG = false;
  DEFAULT_POLL_INTERVAL_IN_SECS = 60; // In Seconds
  VERSION = '0.1.1';

  // Properties
  _deviceNotifiedById = {};
  _deviceLastUpdatedById = {};
  // Authoritative list of devices+status from the status collection (source of truth for the Attention Required modal)
  _deviceStatusList = [];
  // Device IDs pre-selected for the Attention Required stalled tab (set from pie chart view)
  _stalledJobDeviceIds = [];

  // CreateJobs-specific device cache (separate from main devices)
  _createJobsDevices = [];
  // Per-tenant cached full lists for CreateJobs (lazy-initialized in fetchCreateJobsDevices but declared here for clarity)
  // Cache entries are stored as { ts: <epoch ms>, data: <deviceArray> }
  _createJobsDevicesCache = {};

  // Time-to-live for createJobs cache entries (5 hours)
  CREATE_JOBS_CACHE_TTL_MS = 5 * 60 * 60 * 1000;

  _tenants =  {
    /**
     * Item Type
     * @property id
     * @property label
     */
    data: [],
    selected: null,
  };
  _configuration = null;

  _packages = {
    query: {
      skip: 0,
      limit: 30,
    },
    headers: [
      {
        key: 'label',
        label: 'Label',
        sortable: true,
        active: true,
        searchLabel: 'Search by Label(s)',
        searchable: 'label',
      }, {
        key: 'version',
        label: 'Version',
        sortable: true,
        active: true,
        searchLabel: 'Search by Version(s)',
        searchable: 'version',
      }, {
        key: 's3Uri',
        label: 'S3 Uri',
        sortable: false,
        active: true,
      }, {
        key: 'signature',
        label: 'Signature',
        sortable: false,
        active: false,
      }, {
        key: 'additional',
        label: 'Additional Info',
        sortable: false,
        active: true,
        renderAsHtml: true
      }, {
        key: 'fileName',
        label: 'File Name',
        sortable: true,
        active: false,
        searchLabel: 'Search by File Name(s)',
        searchable: 'fileName',
      }, {
        key: 'zipSourceFolder',
        label: 'Zip Source Folder',
        sortable: false,
        active: false,
      }, {
        key: 'pathOnSensor',
        label: 'Package Path',
        sortable: false,
        active: false,
      }, {
        key: 'command',
        label: 'Command',
        sortable: false,
        active: false,
      }, {
        key: 'created',
        label: 'Created',
        sortable: true,
        active: true,
        searchLabel: 'Search within Date Range',
        searchType: 'dateRange',
        searchable: 'created',
      }, {
        key: 'createdBy',
        label: 'Created By',
        sortable: true,
        active: true,
        searchLabel: 'Search by User',
        searchable: 'createdBy',
      },
      {
      key: 'action',
      label: 'Action',
      sortable: false,
      active: true,
    },
    ],
    total: 0,
    /**
     * Item Type
     * @property s3Uri
     * @property signature
     * @property signatureS3Uri
     * @property fileName
     * @property label
     * @property version
     * @property created
     * @property modified
     */
    data: [],
  };
  _devices = {
    searchValue: '',
    query: {
      skip: 0,
      limit: 50,
      location: [],
      unit: [],
      area: [],
      model: [],
      firmwareVersion: "",
    },
    headers: [
      {
        key: 'status',
        label: 'Status',
        sortable: true,
        active: true,
        renderAsHtml: true,
      },
      {
        key: 'tenant',
        label: 'Tenant',
        sortable: false,
        active: true,
        searchable: 'scope',
        searchLabel: 'Select Tenant',
        searchType: 'options',
        searchItems: [],
      },
      {
        key: 'deviceId',
        label: 'Device ID',
        sortable: true,
        active: true,
        renderAsHtml: false,
        searchLabel: 'Search by Device ID(s)',
        searchable: 'deviceId',
      },{
        key: 'serial',
        label: 'MAC',
        sortable: true,
        active: true,
        renderAsHtml: false,
        searchLabel: 'Search by MAC(s)',
        searchable: 'serial',
      }, {
        key: 'firmwareVersionFormatted',
        label: 'Version(s)',
        default: '0.0.0',
        sortable: true,
        active: true,
        renderAsHtml: true,
        searchLabel: 'Search by Version(s)',
        searchable: 'firmwareVersion',
        expandableKey: 'firmwareVersionDetails',
      }, {
        key: 'poc',
        label: 'Location',
        sortable: false,
        active: true,
        searchLabel: 'Filter POC(s)',
        searchable: 'pocTree',
        treeData: [],
        filterActive: false,
      }, {
        key: 'location',
        label: 'Facility',
        sortable: true,
        active: false,
        searchLabel: 'Search by Facility(s)',
        searchable: 'location',
        renderAsHtml: true,
      }, {
        key: 'unit',
        label: 'Area',
        sortable: true,
        active: false,
        searchLabel: 'Search by Area(s)',
        searchable: 'unit',
        renderAsHtml: true,
      }, {
        key: 'area',
        label: 'Location',
        sortable: true,
        active: false,
        searchLabel: 'Search by Location(s)',
        searchable: 'area',
        renderAsHtml: true,
      },
      {
        key: 'lastSeen',
        label: 'Last Seen',
        sortable: true,
        active: true,
      },
      {
        key: 'provisionedOn',
        label: 'Provisioned On',
        sortable: true,
        active: true,
      },
    ],
    data: [],
    total: 0,
  };
  _deviceStatusById = {};
  _locations = {
    query: {
      skip: 0,
      limit: 30,
    },
    data: [],
  };
  _jobs = {
    query: {
      skip: 0,
      limit: 30,
    },
    headers: [
      {
        key: 'tenant',
        label: 'Tenant',
        sortable: false,
        active: true,
        searchable: 'scope',
        searchLabel: 'Select Tenant',
        searchType: 'options',
        searchItems: [],
      },
      {
        key: '_id',
        label: 'ID',
        sortable: false,
        active: true,
        searchLabel: 'Search by Job ID',
        searchable: '_id',
      }, {
        key: 'status',
        label: 'Status',
        sortable: true,
        active: true,
        renderAsHtml: true,
        searchLabel: 'Search by Status(s)',
        searchable: 'status',
        searchItems: [{
          value: 'inProgress',
          text: 'In Progress'
        }, {
          value: 'success',
          text: 'Success'
        }, {
          value: 'failed',
          text: 'Failed'
        }],
        searchType: 'options',
        searchMulti: true
      }, {
        key: 'tasksFormatted',
        label: 'Task List',
        sortable: false,
        active: true,
        renderAsHtml: true,
        searchLabel: 'Search by Task Type(s)',
        searchable: 'taskType',
        searchItems: [],
        searchType: 'options',
        searchMulti: true
      }, {
        key: 'tasksPackages',
        label: 'Task Details',
        sortable: false,
        active: true,
        renderAsHtml: true,
        searchable: 'packageId',
        searchLabel: 'Search for Package(s)',
        searchItems: [],
        searchType: 'options',
        searchMulti: true,
        searchCallback: async (slug) => {
          this.fetchPackages(slug);
        }
      }, {
        key: 'lastUpdatedAtReduced',
        label: 'Last Updated',
        sortable: false,
        active: true,
      }, {
        key: 'createdFormatted',
        label: 'Created',
        sortable: true,
        sortKey: 'created',
        active: true,
        searchLabel: 'Search within Date Range',
        searchType: 'dateRange',
        searchable: 'created',
      },
      {
        key: 'createdBy',
        label: 'Created By',
        sortable: true,
        active: true,
        searchLabel: 'Search by User',
        searchable: 'createdBy',
      }
    ],
/**
     * Item Type
     * @property id
     * @property deviceList
     * @property taskList
     * @property created
     */
    data: [],
    taskStatusMetrics: []
  };
  _selectedJob = {
    headers: [
      {
        key: 'deviceStatus',
        label: 'Device Status',
        sortable: true,
        active: true,
        renderAsHtml: true,
      },
      {
       key: 'id',
      label: 'Device',
      renderAsHtml: false,
      sortable: true,
      active: true,
      searchLabel: 'Search by Device ID',
      searchable: 'id',
      }, {
        key: 'ipPrefix',
        label: 'Device IP Prefix',
        sortable: true,
        active: false,
      },  {
        key: 'taskType',
        label: 'Task Type',
        sortable: false,
        active: true,
        renderAsHtml: true,
      }, {
        key: 'taskStatus',
        label: 'Task Status',
        sortable: true,
        active: true,
        renderAsHtml: true,
      }, {
        key: 'taskDetails',
        label: 'Task Details',
        sortable: false,
        active: true,
        renderAsHtml: true,
      }, {
        key: 'lastUpdatedAt',
        label: 'Last Updated',
        sortable: true,
        active: true,
      },
      {
        key: 'action',
        label: 'Action',
        sortable: false,
        active: true,
        renderAsHtml: true,
      },
    ],
    data: [],
    taskStatusMetrics: [],
    displayMetrice: false
  };
  _selectedDevice = {
    headers: [
      {
        key: 'id',
        label: 'Device ID',
        sortable: true,
        active: true,
      },
    ],
    data: []
  };
  _actionQueue = [];
  _tabIndex = 0;
  _previousTabIndex = 0;

  constructor() {
    if (this._DEBUG) {
      this.debugMessage('BOOTSTRAPPED DM SERVICE');
      this.bootstrapDebugData();
    }
  }

  bootstrapDebugData() {
      // MOCK PACKAGES
    for (let i = 0; i < 31; i += 1) {
      this.packages.data.push({
        id: (Math.random() + 1).toString(36).substring(7),
        s3Uri: (Math.random() + 1).toString(36).substring(7),
        signature: (Math.random() + 1).toString(36).substring(7),
        signatureS3Uri: (Math.random() + 1).toString(36).substring(7),
        fileName: (Math.random() + 1).toString(36).substring(7),
        label: (Math.random() + 1).toString(36).substring(7),
        version: (Math.random() + 1).toString(36).substring(7),
        createdBy: (Math.random() + 1).toString(36).substring(7),
        created: new Date(),
        modified: new Date(),
      })
    }
    this.packages = { ...this.packages };

    // MOCK DEVICES
    for (let i = 0; i < 85; i += 1) {
      this.devices.data.push({
        deviceId: (Math.random() + 1).toString(36).substring(7),
        tenant: (Math.random() + 1).toString(36).substring(1),
        model: 'ProCuity 3009',
        poc: {
          location: (Math.random() + 1).toString(36).substring(1),
          unit: (Math.random() + 1).toString(36).substring(1),
          area: (Math.random() + 1).toString(36).substring(1),
        },
        lastSeen: new Date(),
      })
    }
    for (let i = 0; i < 16; i += 1) {
      this.devices.data.push({
        deviceId: (Math.random() + 1).toString(36).substring(7),
        tenant: (Math.random() + 1).toString(36).substring(1),
        model: 'ProCuity 3009',
        poc: null, // Unassigned devices must be supported
        lastSeen: new Date(),
      })
    }
    this.devices = { ...this.devices };

    // MOCK LOCATIONS
    for (let i = 0; i < 100; i += 1) {
      this.locations.data.push({
        tenant: (Math.random() + 1).toString(36).substring(1),
        location: (Math.random() + 1).toString(36).substring(1),
        unit: (Math.random() + 1).toString(36).substring(1),
        area: (Math.random() + 1).toString(36).substring(1),
      })
    }
    this.locations = { ...this.locations };

    // MOCK TENANTS
    for (let i = 0; i < 100; i += 1) {
      this.tenants.data.push({
        id: (Math.random() + 1).toString(36).substring(1),
        label: (Math.random() + 1).toString(36).substring(3),
      })
    }
    this.tenants = { ...this.tenants };

    // MOCK JOBS
    for (let i = 0; i < 16; i += 1) {
      this.jobs.data.push({
        id: (Math.random() + 1).toString(36).substring(3),
        deviceList: [
          (Math.random() + 1).toString(36).substring(3),
          (Math.random() + 1).toString(36).substring(3),
          (Math.random() + 1).toString(36).substring(3),
        ],
        taskList: [
          {
            type: "OTA",
            packageID: (Math.random() + 1).toString(36).substring(3)
          },
          {
            type: "OTA",
            packageID: (Math.random() + 1).toString(36).substring(3)
          },
          {
            type: "OTA",
            packageID: (Math.random() + 1).toString(36).substring(3)
          }
        ],
        createdBy: (Math.random() + 1).toString(36).substring(7),
        created: new Date(),
      })
    }
    this.jobs = { ...this.jobs };
      }

  debugMessage(message) {
    if (this._DEBUG) {
      console.log(message);
    }
  }

  get appVersion() {
    return `v${BM_VERSION} BM`;
  }

  // Property Getters & Setters
  get deviceNotifiedById() {
  return this._deviceNotifiedById;
}

  get deviceLastUpdatedById() {
    return this._deviceLastUpdatedById;
  }

  get stalledJobDeviceIds() {
    return this._stalledJobDeviceIds;
  }

  set stalledJobDeviceIds(value) {
    this._stalledJobDeviceIds = Array.isArray(value) ? value : [];
  }

  get tenants() {
    return this._tenants;
  }

  set tenants(value) {
    this._tenants = {...value};
  }

  get packages() {
    return this._packages;
  }

  set packages(value) {
    this._packages = {...value};
  }

  fetchPackages(search = null) {
    Vue.prototype.$DeviceManagerAPIService.getPackages(search ? { search } : this.packages.query).then((res) => {
      if (res && Array.isArray(res.items)) {
        const taskPackagesHeader = this.jobs.headers.find((header) => header.key === 'tasksPackages');
        if (search !== null || (taskPackagesHeader && !taskPackagesHeader.searchItems.length)) {
          if (taskPackagesHeader && res.items.length) {
            res.items.forEach((item) => {
              const found = taskPackagesHeader.searchItems.find((existing) => existing.value === item._id);
              if (!found) taskPackagesHeader.searchItems.push({ text: `${item.label} (${item.version})`, value: item._id })
            })
          }
        }
      if (search === null) {
        const items = res.items.map((item) => ({
          ...item,
          action: item.isArchieved ? 'Unarchive' : 'Archive'
        }));

        this.packages.data = items;
        this.packages.total = res.count;
      }
      }
    })
  }

  get devices() {
    // Ensure query object always present with same defaults to avoid undefined limit issues
    if (!this._devices) {
      this._devices = { query: { skip: 0, limit: 50 }, headers: [], data: [], total: 0 };
    } else if (!this._devices.query || typeof this._devices.query !== 'object') {
      this._devices.query = { skip: 0, limit: 50 };
    } else {
      if (this._devices.query.limit === undefined || this._devices.query.limit === null) this._devices.query.limit = 50;
      if (this._devices.query.skip === undefined || this._devices.query.skip === null) this._devices.query.skip = 0;
    }
    return this._devices;
  }

  set devices(value) {
    // Preserve existing query.limit/skip when external code replaces the devices object
    const prev = this._devices || {};
    const prevQuery = prev.query || {};
    const incoming = value && typeof value === 'object' ? { ...value } : {};

    if (incoming.query && typeof incoming.query === 'object') {
      // merge with previous query so we don't drop limit/skip accidentally
      incoming.query = { ...prevQuery, ...incoming.query };
      if (incoming.query.limit === undefined || incoming.query.limit === null) incoming.query.limit = prevQuery.limit || 50;
      if (incoming.query.skip === undefined || incoming.query.skip === null) incoming.query.skip = prevQuery.skip || 0;
    } else {
      // ensure query exists
      incoming.query = { ...prevQuery };
      if (incoming.query.limit === undefined || incoming.query.limit === null) incoming.query.limit = 50;
      if (incoming.query.skip === undefined || incoming.query.skip === null) incoming.query.skip = 0;
    }

    this._devices = { ...incoming };
  }

  get deviceStatusById() {
    return this._deviceStatusById;
  }

  get deviceStatusList() {
    return this._deviceStatusList;
  }

  // CreateJobs device cache getters
  get createJobsDevices() {
    return this._createJobsDevices;
  }

  set createJobsDevices(value) {
    this._createJobsDevices = value;
  }

   updatePackageArchiveStatus(payload) {
   Vue.prototype.$DeviceManagerAPIService.updatePackageArchiveStatus(payload);
  this.fetchPackages();
}

  fetchDevices() {
    if (this.tenants.selected && this.configuration && this.configuration.tasks) {
      const message = {};
      const numericFields = ['limit', 'skip'];
      const query = Object.keys(this.devices.query).reduce((res, val) => {
        if (val === 'area') {
          message.area = this.devices.query[val];
        } else if (numericFields.includes(val)) {
          res[val] = this.devices.query[val];
        } else if (this.devices.query[val] && this.devices.query[val].length) {
          res[val] = this.devices.query[val];
        }
        return res;
      }, {});
      if (!query.model || !query.model.length) {
        query.model = Array.from(this.configuration.tasks.reduce((iterator, val) => {
          if (Array.isArray(val.models)) {
            val.models.forEach((model) => iterator.add(model));
          }
          return iterator;
        }, new Set()));
      }

      Vue.prototype.$DeviceAPIService.getBedsWithAttributes(this.tenants.selected.value, query, message).then((res) => {
        const devices = res && Array.isArray(res.data) ? res.data.map((device) => {
          device.tenant = this.tenants.selected.value;
          device.key = `${this.tenants.selected.value}-${device.deviceId}-${device.registeredOn}`
          return device;
        }) : [];
        this.devices.data = devices;
        this.devices.total = res && res.count ? res.count : 0;
      });
    }
  }

  // Fetches every device matching the current devices.query filters (ignoring the UI's
  // skip/limit pagination), paging through the API under the hood. Used for "export all" style
  // actions where the user wants the full filtered result set rather than just the current page.
  async fetchAllDevicesForExport(pageSize = 200) {
    if (!this.tenants.selected || !this.configuration || !this.configuration.tasks) {
      return [];
    }

    const message = {};
    const baseQuery = {};
    Object.keys(this.devices.query).forEach((key) => {
      const value = this.devices.query[key];
      if (key === 'area') {
        message.area = value;
      } else if (key === 'limit' || key === 'skip') {
        // Pagination is managed locally below, not forwarded from the UI query.
      } else if (value && value.length) {
        baseQuery[key] = value;
      }
    });
    if (!baseQuery.model || !baseQuery.model.length) {
      const models = new Set();
      this.configuration.tasks.forEach((task) => {
        if (Array.isArray(task.models)) {
          task.models.forEach((model) => models.add(model));
        }
      });
      baseQuery.model = Array.from(models);
    }

    const tenant = this.tenants.selected.value;
    const allDevices = [];
    let skip = 0;
    let total = Infinity;

    while (skip < total) {
      const query = { ...baseQuery, skip, limit: pageSize };
      // eslint-disable-next-line no-await-in-loop
      const res = await Vue.prototype.$DeviceAPIService.getBedsWithAttributes(tenant, query, message);
      // getBedsWithAttributes (via fetchWithBackoff) resolves to undefined on auth failures,
      // non-ok responses, or network errors - indistinguishable from a real empty page unless
      // checked explicitly. Treat that as a hard failure instead of silently truncating the
      // export, otherwise a mid-export failure looks like a complete but short CSV.
      if (!res || !Array.isArray(res.data)) {
        throw new Error(`Failed to fetch devices for export at skip=${skip}.`);
      }
      const pageItems = res.data;
      pageItems.forEach((device) => {
        device.tenant = tenant;
        allDevices.push(device);
      });
      total = typeof res.count === 'number' ? res.count : allDevices.length;
      if (!pageItems.length) break; // Safety net in case count is unreliable.
      skip += pageSize;
    }

    return allDevices;
  }

  fetchDevicesbyId(deviceId) {
    if (this.tenants.selected && this.configuration && this.configuration.tasks) {
      const message = {};
      const query = { deviceId };

      Vue.prototype.$DeviceAPIService.getBedsWithAttributes(this.tenants.selected.value, query, message).then((res) => {
        const devices = res && Array.isArray(res.data) ? res.data.map((device) => {
          device.tenant = this.tenants.selected.value;
          device.key = `${this.tenants.selected.value}-${device.deviceId}-${device.registeredOn}`
          return device;
        }) : [];
        this.devices.data = [...this.devices.data, ...devices];
        this.devices.total = this.devices.total + ((res && res.count) ? res.count : 0);
        this.fetchDevicesAndStatus(this.tenants.selected.value)
      });
    }
  }

  fetchAllDevices() {
    if (this.tenants.selected && this.configuration && this.configuration.tasks) {
      const message = {};
      const query = {};
      if (!query.model || !query.model.length) {
        query.model = Array.from(this.configuration.tasks.reduce((iterator, val) => {
          if (Array.isArray(val.models)) {
            val.models.forEach((model) => iterator.add(model));
          }
          return iterator;
        }, new Set()));
      }

      Vue.prototype.$DeviceAPIService.getBedsWithAttributes(this.tenants.selected.value, query, message).then((res) => {
        const devices = res && Array.isArray(res.data) ? res.data.map((device) => {
          device.tenant = this.tenants.selected.value;
          device.key = `${this.tenants.selected.value}-${device.deviceId}-${device.registeredOn}`
          return device;
        }) : [];
      });
    }
  }

  get locations() {
    return this._locations;
  }

  set locations(value) {
    this._locations = {...value};
  }

  get jobs() {
    return this._jobs;
  }

  set jobs(value) {
    this._jobs = {...value};
  }

  fetchJobs() {
    if (this.tenants.selected && this.tenants.selected.value) {
      Vue.prototype.$DeviceManagerAPIService.getJobs({...this.jobs.query, scope: this.tenants.selected.value}).then((res) => {
        this.jobs.data = res.items;
        this.jobs.total = res.count;
      });
    }
  }

  fetchJobById(jobId) {
    if (this.tenants.selected && this.tenants.selected.value) {
      return Vue.prototype.$DeviceManagerAPIService.getJobs({ jobId, scope: this.tenants.selected.value });
    }
    return Promise.resolve(null);
  }

  cancelJobDeviceTask({ jobId, batchJobId, deviceId, taskId }) {
    if (!this.tenants.selected || !this.tenants.selected.value) {
      return Promise.reject('No tenant selected.');
    }
    return Vue.prototype.$DeviceManagerAPIService.cancelJobDeviceTask({
      tenant: this.tenants.selected.value,
      jobId,
      batchJobId,
      deviceId,
      taskId,
    });
  }

  cancelJobRequest({ requestId }) {
    if (!this.tenants.selected || !this.tenants.selected.value) {
      return Promise.reject('No tenant selected.');
    }
    return Vue.prototype.$DeviceManagerAPIService.cancelJobRequest({
      tenant: this.tenants.selected.value,
      requestId,
    });
  }

  get selectedJob() {
    return this._selectedJob;
  }

  set selectedJob(value) {
    this._selectedJob = {...value};
  }

  get selectedDevice() {
    return this._selectedDevice;
  }

  set selectedDevice(value) {
    this._selectedDevice = {...value};
  }

  fetchDeviceAttributes() {
    if (this.tenants.selected) {
      Vue.prototype.$DeviceAPIService.getDeviceAttributes(this.tenants.selected.value, this.selectedDevice.id).then((res) => {
        this.selectedDevice.data = res;
        this.devices.total = res.count;
      });
    }
  }

  get configuration() {
    return this._configuration;
  }

  set configuration(value) {
    this._configuration = {...value};
  }

  get jobStatusClassification() {
    if (!this._configuration || !this._configuration.jobStatusClassification) return {};
    const raw = this._configuration.jobStatusClassification;
    if (!this._jobStatusClassificationCache || this._jobStatusClassificationCacheSource !== raw) {
      this._jobStatusClassificationCacheSource = raw;
      const cache = {};
      for (const taskType of Object.keys(raw)) {
        const entry = raw[taskType];
        cache[taskType] = {
          success: new Set(entry.success || []),
          failed: new Set(entry.failed || []),
          inProgress: new Set(entry.inProgress || []),
        };
      }
      this._jobStatusClassificationCache = cache;
    }
    return this._jobStatusClassificationCache;
  }

  fetchConfiguration() {
    Vue.prototype.$DeviceManagerAPIService.getConfiguration().then((res) => {
      this.configuration = res;
      if (Array.isArray(res.tasks)) {
        const header = this.jobs.headers.find((header) => header.key === 'tasksFormatted');
        header.searchItems = [];
        res.tasks.forEach((task) => {
          header.searchItems.push({text: task.type, value: task.type});
        })
      }
    });
  }

 fetchDevicesAndStatus(selectedTenant) {
  if (selectedTenant) {
    const query = { scope: selectedTenant, statuses: []}
    // eslint-disable-next-line consistent-return
    return Vue.prototype.$DeviceManagerAPIService.getDevicesByStatusAndTenant({ ...query }).then(res => {
      // Only skip on a missing/malformed response (network failure) so state isn't wiped by a
      // transient error - a valid empty array (tenant genuinely has no devices) must still clear
      // out the previous tenant's stale data below.
      if (res && Array.isArray(res)) {
        const statusMap = {};
        const notifiedMap = {};
        const lastUpdatedMap = {};
        const devicesWithStatus = [];

        res.forEach(device => {
          statusMap[device._id] = device.status;
          statusMap['_' + device._id] = device.jobId;
          notifiedMap[device._id] = device.isNotified;
          lastUpdatedMap[device._id] = device.lastUpdatedTimestamp;
          devicesWithStatus.push({ deviceId: device._id, status: device.status, jobId: device.jobId, gatewaySWVersionNumber: device.gatewaySWVersionNumber });
        });
        this._deviceStatusById = statusMap;
        this._deviceNotifiedById = notifiedMap;
        this._deviceLastUpdatedById = lastUpdatedMap;
        this._deviceStatusList = devicesWithStatus;

        this.updatedDevicesState(devicesWithStatus);
      }
    });
  }
}

  fetchComponentAndVersions(selectedTenant) {
    if (selectedTenant) {
      const query = { scope: selectedTenant}
      return Vue.prototype.$DeviceManagerAPIService.getComponentAndVersions({ ...query });
    }
  }

  updatedDevicesState(devicesWithStatus) {
    const validStatus = new Set(OTA_TERMINAL_STATUSES.map(s => s.toLowerCase()));
    const cutoffVersion = this.configuration && this.configuration.phase2GWVersion;
    this._createJobsDevices = this._createJobsDevices.map(d => {
      const match = Array.isArray(devicesWithStatus)
        ? devicesWithStatus.find(dev => dev.deviceId === d.deviceId)
        : null;

      // Pre-phase2 gateways (gatewaySWVersionNumber < config.phase2GWVersion) are exempt from the
      // busy-device restriction and can be added to a job anytime. No cutoff or no reported version
      // means we can't confirm the gateway is old, so the normal restriction still applies.
      const isPrePhase2 = !!(cutoffVersion && match && match.gatewaySWVersionNumber
        && compareGatewayVersions(match.gatewaySWVersionNumber, cutoffVersion) < 0);

      let isDisable = false;
      if (!isPrePhase2 && match && match.status) {
        isDisable = !validStatus.has(String(match.status).toLowerCase());
      }
      return {
        ...d,
        disable: isDisable
      };
    })
  }

  exportToCSV(csvContent, fileName) {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;'});
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    const timestamp = new Date().toISOString().replace(/T/, '_').replace(/:/g, '-').slice(0, 16);

    link.setAttribute('href', url);
    link.setAttribute('download', `${fileName}_${timestamp}.csv`);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  async extractZipContent(file) {
  const zip = new JSZip();
  const decodedZip = await zip.loadAsync(file);
  const root = {};

  const entries = Object.keys(decodedZip.files);

  for (const path of entries) {
    const entry = decodedZip.files[path];
    const pathParts = path.split('/').filter(p => p.length > 0);
    let currentLevel = root;

    for (let i = 0; i < pathParts.length; i++) {
      const part = pathParts[i];
      const isLastPart = i === pathParts.length - 1;

      if (isLastPart && !entry.dir) {
        // --- NEW LOGIC FOR NESTED ZIPS ---
        if (part.toLowerCase().endsWith('.zip')) {
          // 1. Get the internal zip as binary data (uint8array)
          const nestedZipData = await entry.async('uint8array');

          // 2. Recursively extract the internal zip
          // This will return the nested tree structure
          currentLevel[part] = await this.extractZipContent(nestedZipData);
        } else {
          // 3. Normal File: Extract as string
          currentLevel[part] = await entry.async('string');
        }
      } else {
        if (!currentLevel[part]) {
          currentLevel[part] = {};
        }
        currentLevel = currentLevel[part];
      }
    }
  }

  return root;
  }

  parseXml(xmlString) {
  const options = {
    ignoreAttributes: false,
    attributeNamePrefix: "",
    parseTagValue: true,
    trimValues: true
  };

  // 1. Parse the XML as usual (results in PascalCase)
  const rawData = parser.parse(xmlString, options);

  // 2. Transform the keys recursively to camelCase
  return this.camelizeKeys(rawData);
}

// Helper to handle the string conversion
  toCamelCase(str) {
  return str
    .replace(/([-_][a-z])/gi, (match) =>
      match.toUpperCase().replace('-', '').replace('_', '').replace('.', '_')
    )
    .replace(/^[A-Z]/, (match) => match.toLowerCase());
}

// Helper to walk through the object/array tree
  camelizeKeys(obj) {
  if (Array.isArray(obj)) {
    return obj.map(v => this.camelizeKeys(v));
  } else if (obj !== null && obj.constructor === Object) {
    return Object.keys(obj).reduce((result, key) => {
      const newKey = this.toCamelCase(key);
      // This is where we add the new key-value pair to our new object
      result[newKey] = this.camelizeKeys(obj[key]);
      return result;
    }, {});
  }
  return obj;
}




  // Fetch devices for CreateJobs using getDevicesByFilter endpoint
  // Accepts optional query overrides (deviceId, models, components, etc.)
  // Fetch devices used by CreateJobs and cache full-list per-tenant.
  // Behavior:
  // - If called without queryOverrides => return cached full list for scope if present, otherwise fetch full list and cache it.
  // - If called with queryOverrides (search / deviceId etc.) => fetch filtered results and do NOT overwrite the cached full-list.
  // - If called with queryOverrides that are effectively 'cleared' (all empty/empty-arrays/null/undefined) => restore cached full-list when available.
  async fetchCreateJobsDevices(scope, queryOverrides = {}) {
    if (!scope) return;

    // Initialize cache container if needed
    if (!this._createJobsDevicesCache) this._createJobsDevicesCache = {};

    const hasOverrides = queryOverrides && Object.keys(queryOverrides).length > 0;

    // Helper to detect "cleared" overrides (treat as request to restore full cached list)
    const overridesAreCleared = () => {
      if (!hasOverrides) return false;
      return Object.values(queryOverrides).every((v) => (
        v === null || v === undefined || v === '' || (Array.isArray(v) && v.length === 0)
      ));
    };

    // Helper to determine if a cache entry is still valid
    const cacheEntryIsValid = (entry) => entry && entry.ts && (Date.now() - entry.ts) < this.CREATE_JOBS_CACHE_TTL_MS;

    // If no overrides and cache exists and is valid, use it
    if (!hasOverrides && cacheEntryIsValid(this._createJobsDevicesCache[scope])) {
      this._createJobsDevices = this._createJobsDevicesCache[scope].data;
      // still update status info
      this.fetchDevicesAndStatus(scope);
      return;
    }

    // If cache exists but expired, remove it so we fetch fresh data
    if (this._createJobsDevicesCache[scope] && !cacheEntryIsValid(this._createJobsDevicesCache[scope])) {
      delete this._createJobsDevicesCache[scope];
    }

    // If overrides are cleared and cache exists and is valid, restore it
    if (overridesAreCleared() && cacheEntryIsValid(this._createJobsDevicesCache[scope])) {
      this._createJobsDevices = this._createJobsDevicesCache[scope].data;
      this.fetchDevicesAndStatus(scope);
      return;
    }

    window.showLoading('Loading Devices...');
    try {
      const params = Object.assign({ scope, skip: 0, model: 'ProCuity-3009' }, queryOverrides || {});

      const response = await Vue.prototype.$DeviceManagerAPIService.getCreateJobsDevices(params);

      if (response && Array.isArray(response.data)) {
        // If this was an unfiltered request (no overrides) then cache the full-list
        if (!hasOverrides) {
          this._createJobsDevicesCache[scope] = { ts: Date.now(), data: response.data };
          this._createJobsDevices = response.data;
        } else {
          // For filtered requests, present the filtered results but keep full cache intact
          this._createJobsDevices = response.data;
        }
        this.fetchDevicesAndStatus(scope);
      }
    } catch (error) {
      console.error('Failed to fetch CreateJobs devices:', error);
    } finally {
      window.hideLoading();
    }
  }

  // Fetch expanded device attributes using lean endpoint (version + bedData only)
  async fetchExpandedDeviceAttributes(tenant, deviceId) {
    if (!tenant || !deviceId) return null;

      const response = await Vue.prototype.$DeviceManagerAPIService.getCompactDeviceAttribute({
        scope: tenant,
        deviceId: deviceId
      });
        return {version: response.version || {}, latestCompletedJob: response.latestCompletedJob || null, bedData: response.bedData || {}};
  }

  get actions() {
    return this._actionQueue;
  }

  set actions(value) {
    this._actionQueue.push(value);
  }

  get tabIndex() {
    return this._tabIndex;
  }

  set tabIndex(value) {
    this._tabIndex = value;
  }

  get previousTabIndex() {
    return this._previousTabIndex;
  }

  set previousTabIndex(value) {
    this._previousTabIndex = value;
  }
};

// Singleton Pattern Instantiate
if (!Vue.prototype.$BedManagerService) {
  Vue.prototype.$BedManagerService = new BedManagerService();
}

export default Vue.prototype.$BedManagerService;
